import {
  type ClientInfo,
  type InteractionConfig,
  type RequestType,
} from '../../../types';
import { type RequestedDataGroup } from '../../../hooks/useRequestedData';
import {
  CharlieAccordion,
  CharlieAccordionContent,
  CharlieAccordionItem,
  CharlieAccordionTrigger,
} from '@ver.id/libs.ui.charlie';
import { useTranslation } from '../../../i18n/i18n';
import HostedSidebarSkeleton from './SidebarSkeleton';
import HostedStepsCard from './StepsCard';
import HostedRequestedDataCard from './RequestedDataCard';
import HostedRequestedDataPlaceholder from './RequestedDataPlaceholder';
import HostedClientLogo from '../ClientLogo';

/** The heading and introduction per request type; signature reads as disclosure. */
const COPY_KEYS: Record<
  RequestType,
  { title: string; description: string; fallback: string }
> = {
  issuance: {
    title: 'sidebar.issuanceTitle',
    description: 'sidebar.issuanceDescription',
    fallback: 'sidebar.issuanceDescriptionFallback',
  },
  authentication: {
    title: 'sidebar.authenticationTitle',
    description: 'sidebar.authenticationDescription',
    fallback: 'sidebar.authenticationDescriptionFallback',
  },
  disclosure: {
    title: 'sidebar.title',
    description: 'sidebar.description',
    fallback: 'sidebar.descriptionFallback',
  },
  signature: {
    title: 'sidebar.title',
    description: 'sidebar.description',
    fallback: 'sidebar.descriptionFallback',
  },
};

type SidebarProps = {
  client?: ClientInfo;
  requestType?: RequestType;
  interactionConfig?: InteractionConfig;
  requestedDataGroups?: RequestedDataGroup[];
  requestedDataLoading?: boolean;
  variant?: 'mobile' | 'desktop';
};

/**
 * HostedSidebar — Desktop left panel showing branding and flow info.
 *
 * Figma: Left sidebar panel visible in all desktop journey frames.
 * Skeleton state: https://www.figma.com/design/6V1D3oNy17pfFNHIRb2nmm/Ronan-V2---UX?node-id=64-7730
 * Loaded state:   https://www.figma.com/design/6V1D3oNy17pfFNHIRb2nmm/Ronan-V2---UX?node-id=339-22201
 */
export default function HostedSidebar({
  client,
  requestType,
  interactionConfig,
  requestedDataGroups,
  requestedDataLoading,
  variant = 'desktop',
}: SidebarProps) {
  const { t } = useTranslation();
  const isIssuance = requestType === 'issuance';

  const copy = COPY_KEYS[requestType ?? 'disclosure'];

  if (!client && !requestType) {
    return <HostedSidebarSkeleton />;
  }

  return (
    <div className="flex flex-col gap-5 w-full h-full">
      {/* Top section: logo + description */}
      <div className="flex flex-col gap-8 pt-10 md:pt-10">
        {/* Client logo (hidden on mobile, already shown in sheet header) */}
        <div className="hidden sidebar:flex items-center justify-start">
          <HostedClientLogo client={client} className="h-12 object-contain" />
        </div>

        {/* Title & description */}
        <div className="flex flex-col gap-4">
          <h2 className="text-2xl md:text-2xl font-semibold text-sidebar-foreground leading-8">
            {t(copy.title)}
          </h2>
          <p className="text-base text-muted-foreground leading-6">
            {client ? (
              <span>
                {t(copy.description, { clientName: client.name })
                  .split(client.name)
                  .map((part, i, arr) => (
                    <span key={i}>
                      {part}
                      {i < arr.length - 1 && <strong>{client.name}</strong>}
                    </span>
                  ))}
              </span>
            ) : (
              t(copy.fallback)
            )}
          </p>
        </div>
      </div>

      {/* Content wrapper: steps + data + policy */}
      <div className="flex flex-1 flex-col gap-9 pb-10">
        <div className="flex flex-1 flex-col gap-9">
          <HostedStepsCard
            clientName={client?.name}
            requestType={requestType}
          />

          {requestedDataGroups && requestedDataGroups.length > 0 ? (
            <HostedRequestedDataCard
              groups={requestedDataGroups}
              isIssuance={isIssuance}
              variant={variant}
            />
          ) : variant !== 'mobile' ? (
            <HostedRequestedDataPlaceholder
              loading={!!requestedDataLoading}
              isIssuance={isIssuance}
            />
          ) : null}

          {(interactionConfig?.purposeStatement ||
            interactionConfig?.retentionPolicy) && (
            <CharlieAccordion type="single" collapsible>
              {interactionConfig.purposeStatement && (
                <CharlieAccordionItem value="purpose">
                  <CharlieAccordionTrigger>
                    {t('sidebar.purposeStatementTitle')}
                  </CharlieAccordionTrigger>
                  <CharlieAccordionContent className="text-muted-foreground">
                    {interactionConfig.purposeStatement}
                  </CharlieAccordionContent>
                </CharlieAccordionItem>
              )}
              {interactionConfig.retentionPolicy && (
                <CharlieAccordionItem value="retention">
                  <CharlieAccordionTrigger>
                    {t('sidebar.retentionPolicyTitle')}
                  </CharlieAccordionTrigger>
                  <CharlieAccordionContent className="text-muted-foreground">
                    {interactionConfig.retentionPolicy}
                  </CharlieAccordionContent>
                </CharlieAccordionItem>
              )}
            </CharlieAccordion>
          )}
        </div>
      </div>
    </div>
  );
}
